import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { useCanViewApiAudit } from "../../hooks/useCanViewApiAudit";
import { getConversationMessagePartnerAudit } from "../../api/client";
import {
  buildAuditCopyBundle,
  formatAuditJson,
  getPartnerDisplayLabel,
  getWebhookPayloadForDisplay,
  isInboundWebhookAudit,
  resolveMessageApiAudit,
} from "../../utils/partnerApiAuditDisplay";
import { sanitizePartnerApiAuditForDisplay } from "../../utils/partnerApiAuditRedact";
import { looksLikePartnerApiLogText } from "./messageTypeRegistry";

async function copyText(text) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
}

const JsonBlock = ({ value, label }) => (
  <Box>
    {label ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
    ) : null}
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        maxHeight: 360,
        overflow: "auto",
        fontSize: "0.7rem",
        lineHeight: 1.45,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        bgcolor: "action.hover",
        borderRadius: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {formatAuditJson(value) || "—"}
    </Box>
  </Box>
);

const parseJsonMaybe = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const PartnerApiAuditPanel = ({ message, compact = false, defaultOpen = false }) => {
  const theme = useTheme();
  const { t } = useTranslation("app");
  const canView = useCanViewApiAudit();
  const embeddedAudit = useMemo(() => {
    const raw = resolveMessageApiAudit(message);
    return raw ? sanitizePartnerApiAuditForDisplay(raw) : null;
  }, [message]);

  const [fetchedAudit, setFetchedAudit] = useState(null);
  const [fetchState, setFetchState] = useState("idle"); // idle | loading | error | empty
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState("");

  const audit = fetchedAudit || embeddedAudit;
  const looksLikeApi =
    Boolean(embeddedAudit) ||
    looksLikePartnerApiLogText(message?.textPlain || message?.text || "") ||
    Boolean(message?.apiAudit) ||
    /partner_api_audit|xpro_api_audit/i.test(String(message?.json_whatsapp || ""));

  // Só usa JSON bruto quando a mensagem já é de auditoria de API.
  // Qualquer json_whatsapp (ex.: xpro_log, payload WhatsApp) não deve abrir este painel —
  // senão master vê "Detalhes técnicos" em quase toda mensagem, às vezes idêntico ao de IA.
  const rawFallback = useMemo(() => {
    if (audit || !looksLikeApi) return null;
    const fromWa = parseJsonMaybe(message?.json_whatsapp);
    if (fromWa && (fromWa.xpro_api_audit || fromWa.kind === "partner_api_audit" || fromWa.partner)) {
      return fromWa;
    }
    const fromAck = parseJsonMaybe(message?.json_status_message_ack);
    if (fromAck && (fromAck.xpro_api_audit || fromAck.kind === "partner_api_audit" || fromAck.partner)) {
      return fromAck;
    }
    return null;
  }, [audit, looksLikeApi, message]);

  const canShow = canView && (looksLikeApi || Boolean(audit));
  const hasLocalPayload = Boolean(audit) || Boolean(rawFallback);

  useEffect(() => {
    if (!open || !canView) return;
    if (audit) {
      setFetchState("idle");
      return;
    }

    const conversationId = message?.conversationId;
    const messageId = message?.id;

    // Já temos JSON local: mostra na hora e só tenta enriquecer em background (sem spinner).
    if (conversationId == null || messageId == null) {
      setFetchState(hasLocalPayload ? "idle" : "empty");
      return;
    }

    let cancelled = false;
    if (!hasLocalPayload) setFetchState("loading");

    getConversationMessagePartnerAudit(conversationId, messageId)
      .then((remote) => {
        if (cancelled) return;
        if (remote && typeof remote === "object") {
          setFetchedAudit(sanitizePartnerApiAuditForDisplay(remote));
          setFetchState("idle");
          return;
        }
        setFetchState(hasLocalPayload ? "idle" : "empty");
      })
      .catch(() => {
        if (cancelled) return;
        setFetchState(hasLocalPayload ? "idle" : "error");
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    canView,
    audit,
    hasLocalPayload,
    message?.conversationId,
    message?.id,
  ]);

  if (!canShow) return null;

  const isWebhook = audit ? isInboundWebhookAudit(audit) : false;
  const webhookPayload = audit ? getWebhookPayloadForDisplay(audit) : null;
  const partnerLabel = audit
    ? getPartnerDisplayLabel(audit.partner)
    : t("chat.partnerApiAudit.genericPartner", "API");
  const httpStatus = audit?.response?.status;
  const duration =
    audit?.durationMs != null && Number.isFinite(Number(audit.durationMs))
      ? `${audit.durationMs}ms`
      : null;

  const handleCopy = async (kind) => {
    let text = "";
    if (kind === "webhook") text = formatAuditJson(webhookPayload);
    else if (kind === "request") text = formatAuditJson(audit?.request);
    else if (kind === "response") text = formatAuditJson(audit?.response);
    else if (kind === "raw") text = formatAuditJson(rawFallback);
    else text = audit ? buildAuditCopyBundle(audit) : formatAuditJson(rawFallback);
    await copyText(text);
    setCopied(kind);
    setTimeout(() => setCopied(""), 2000);
  };

  const showStructured = Boolean(audit);
  const showRawOnly = !showStructured && Boolean(rawFallback);

  return (
    <Box sx={{ mt: compact ? 0.5 : 0.75, width: "100%", textAlign: "left" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.5,
          justifyContent: "center",
        }}
      >
        <Button
          size="small"
          variant={open ? "outlined" : "contained"}
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          startIcon={<CodeOutlinedIcon sx={{ fontSize: 16 }} />}
          endIcon={
            fetchState === "loading" && !audit && !rawFallback ? (
              <CircularProgress size={12} color="inherit" />
            ) : open ? (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            textTransform: "none",
            fontSize: "0.72rem",
            minWidth: 0,
            px: 1,
            py: 0.25,
            boxShadow: "none",
          }}
        >
          {t("chat.partnerApiAudit.technicalDetails", "Detalhes técnicos")}
        </Button>
        <Chip size="small" label={partnerLabel} sx={{ height: 20, fontSize: "0.65rem" }} />
        {isWebhook ? (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label={t("chat.partnerApiAudit.webhookBadge", "Webhook")}
            sx={{ height: 20, fontSize: "0.65rem" }}
          />
        ) : null}
        {audit?.operation ? (
          <Chip
            size="small"
            variant="outlined"
            label={audit.operation}
            sx={{ height: 20, fontSize: "0.65rem" }}
          />
        ) : null}
        {httpStatus != null ? (
          <Chip
            size="small"
            color={httpStatus >= 400 || audit?.error ? "error" : "default"}
            label={`HTTP ${httpStatus}`}
            sx={{ height: 20, fontSize: "0.65rem" }}
          />
        ) : null}
        {duration ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
            {duration}
          </Typography>
        ) : null}
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 0.75,
            p: 1,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            textAlign: "left",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {fetchState === "loading" && !audit && !rawFallback ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {t("chat.partnerApiAudit.loading", "Carregando auditoria…")}
              </Typography>
            </Box>
          ) : null}

          {fetchState === "error" && !audit && !rawFallback ? (
            <Typography variant="caption" color="error.main">
              {t("chat.partnerApiAudit.loadError", "Não foi possível carregar os detalhes desta auditoria.")}
            </Typography>
          ) : null}

          {fetchState === "empty" && !audit && !rawFallback ? (
            <Typography variant="caption" color="text.secondary">
              {t(
                "chat.partnerApiAudit.unavailable",
                "Não há payload estruturado (request/response) para este log."
              )}
            </Typography>
          ) : null}

          {showStructured || showRawOnly ? (
            <>
              <Box sx={{ display: "flex", gap: 0.5, mb: 0.75, flexWrap: "wrap" }}>
                {showStructured && isWebhook ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleCopy("webhook")}
                    sx={{ fontSize: "0.68rem" }}
                  >
                    {t("chat.partnerApiAudit.copyWebhookJson", "Copiar JSON webhook")}
                  </Button>
                ) : null}
                {showStructured && !isWebhook ? (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopy("request")}
                      sx={{ fontSize: "0.68rem" }}
                    >
                      {t("chat.partnerApiAudit.copyRequest", "Copiar request")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopy("response")}
                      sx={{ fontSize: "0.68rem" }}
                    >
                      {t("chat.partnerApiAudit.copyResponse", "Copiar response")}
                    </Button>
                  </>
                ) : null}
                {showRawOnly ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleCopy("raw")}
                    sx={{ fontSize: "0.68rem" }}
                  >
                    {t("chat.partnerApiAudit.copyRaw", "Copiar JSON bruto")}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  variant={isWebhook || showRawOnly ? "outlined" : "contained"}
                  onClick={() => handleCopy("all")}
                  sx={{ fontSize: "0.68rem" }}
                >
                  {t("chat.partnerApiAudit.copyAll", "Copiar tudo")}
                </Button>
                {copied ? (
                  <Typography variant="caption" color="success.main" sx={{ alignSelf: "center" }}>
                    {t("chat.partnerApiAudit.copied", "Copiado!")}
                  </Typography>
                ) : null}
              </Box>

              {showStructured && isWebhook ? (
                <>
                  <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                      minHeight: 32,
                      mb: 0.5,
                      "& .MuiTab-root": { minHeight: 32, py: 0.25, fontSize: "0.72rem" },
                    }}
                  >
                    <Tab label={t("chat.partnerApiAudit.webhookJsonTab", "JSON recebido")} />
                    <Tab label={t("chat.partnerApiAudit.webhookHeadersTab", "Headers")} />
                    <Tab label={t("chat.partnerApiAudit.platformResponseTab", "Resposta plataforma")} />
                  </Tabs>
                  {tab === 0 ? <JsonBlock value={webhookPayload} /> : null}
                  {tab === 1 ? <JsonBlock value={audit.request?.headers} /> : null}
                  {tab === 2 ? <JsonBlock value={audit.response} /> : null}
                </>
              ) : null}

              {showStructured && !isWebhook ? (
                <>
                  <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                      minHeight: 32,
                      mb: 0.5,
                      "& .MuiTab-root": { minHeight: 32, py: 0.25, fontSize: "0.72rem" },
                    }}
                  >
                    <Tab label={t("chat.partnerApiAudit.requestTab", "Request")} />
                    <Tab label={t("chat.partnerApiAudit.responseTab", "Response")} />
                    {rawFallback ? (
                      <Tab label={t("chat.partnerApiAudit.rawTab", "JSON bruto")} />
                    ) : null}
                  </Tabs>
                  {tab === 0 ? <JsonBlock value={audit.request} /> : null}
                  {tab === 1 ? <JsonBlock value={audit.response} /> : null}
                  {tab === 2 && rawFallback ? <JsonBlock value={rawFallback} /> : null}
                </>
              ) : null}

              {showRawOnly ? (
                <JsonBlock
                  value={rawFallback}
                  label={t("chat.partnerApiAudit.rawPayload", "Payload armazenado")}
                />
              ) : null}

              {audit?.error ? (
                <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.75 }}>
                  {audit.error}
                </Typography>
              ) : null}
            </>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PartnerApiAuditPanel;
